'use client';
import classnames from 'classnames';
import React from 'react';

import { Watchlist, WatchlistId, WatchlistManager, WatchlistManagerEvent } from '@benzinga/watchlist-manager';
import { assertUnreachable, StockSymbol } from '@benzinga/fission';
import Hooks from '@benzinga/hooks';
import { isEmpty, isNil, arrayDifference } from '@benzinga/utils';
import styled, { TC } from '@benzinga/themetron';

import { createSymbol } from '@benzinga/autocomplete-manager';
import { TagSearch, Tooltip, useSymbolSearchModule, useDropdownTool, useClipboardTool } from '@benzinga/pro-ui';
import { isSymbolSearchItem, createSymbolSearchItem, SymbolSearchItem, SearchItem } from '@benzinga/search-modules';

import { WatchlistAlerts } from './alerts';
import { WatchlistCreate } from './create';
import { WatchlistDelete } from './delete';
import { WatchlistEdit } from './edit';
import { WatchlistLink } from './link';
import { WatchlistImport } from './import';
import { WatchlistGrid } from './grid';
import { SessionContext } from '@benzinga/session-context';
import { faBooksMedical } from '@fortawesome/pro-solid-svg-icons/faBooksMedical';
import { Icon } from '@benzinga/core-ui';
import { faBell } from '@fortawesome/pro-solid-svg-icons';
import { Tooltip as TT } from 'antd';
import { Add, Link, Download, UpArrow, Alert, Edit, Trash } from '@benzinga/themed-icons';

interface Props {
  gridSubscriberRef: React.MutableRefObject<React.ElementRef<typeof WatchlistGrid> | null>;
  onSelectWatchlist: (watchlistId: WatchlistId) => void;
  removeSymbolsFromList: (symbol: StockSymbol[]) => void;
  selectedWatchlistId?: WatchlistId;
}

enum ModalType {
  Alerts,
  Create,
  Link,
  Delete,
  Edit,
  Import,
  None,
}

export const WatchlistToolbar: React.FC<Props> = props => {
  const [state, setState] = React.useState({
    activeModal: ModalType.None,
  });

  const session = React.useContext(SessionContext);
  const watchlistManager = session.getManager(WatchlistManager);

  React.useEffect(() => {
    watchlistManager.getWatchlists();
  }, [watchlistManager]);

  const [watchlists, setWatchlists] = React.useState(() => watchlistManager.getStoredWatchlists());

  const watchlist = props.selectedWatchlistId
    ? watchlistManager.getStoredWatchlistById(props.selectedWatchlistId)
    : undefined;

  Hooks.useSubscriber(watchlistManager, (event: WatchlistManagerEvent) => {
    switch (event.type) {
      case 'watchlist:updated_watchlists':
        setWatchlists(event.watchlists);
    }
  });

  const exportToCSV = () => {
    const { gridSubscriberRef } = props;

    if (gridSubscriberRef.current) {
      gridSubscriberRef.current.exportToCSV();
    }
  };

  const setSavedGridParameters = () => {
    const { gridSubscriberRef } = props;

    if (gridSubscriberRef.current) {
      gridSubscriberRef.current.setSavedGridParameters();
    }
  };

  const selectWatchlist = (watchlistId: WatchlistId) => {
    props.onSelectWatchlist(watchlistId);
    setSavedGridParameters();
  };

  const setActiveModal = (modalType: ModalType) => () => {
    setState(old => ({ ...old, activeModal: modalType }));
  };

  const closeModal = setActiveModal(ModalType.None);

  const promptWatchlistCreateIfEmpty = () => {
    if (isNil(watchlists) || watchlists.length === 0 || !props.selectedWatchlistId) {
      setActiveModal(ModalType.Create)();
    }
  };

  const isWatchlistAlertEnabled = (watchlist?: Watchlist) => {
    return (
      watchlist?.emailSummary ||
      watchlist?.realtimeBZ ||
      watchlist?.realtimePR ||
      watchlist?.realtimeSEC ||
      watchlist?.sound !== ''
    );
  };

  const createDropdownMenu = (watchlists: Watchlist[]) =>
    (watchlists || [])
      .map<TC.DropdownMenuItem>((watchlist: Watchlist) => {
        return {
          id: watchlist.watchlistId,

          onClick: () => {
            selectWatchlist(watchlist.watchlistId);
          },
          value: {
            render: (
              <TT mouseEnterDelay={0.5} placement={'bottom'} title={watchlist?.name ?? ''}>
                <div style={{ display: 'flex', flexWrap: 'nowrap' }}>
                  <BellIcon $padding={'0px 5px 0px 0px'}>
                    {isWatchlistAlertEnabled(watchlist) ? (
                      <Icon className={'WLBellIcon'} icon={faBell} />
                    ) : (
                      <div style={{ margin: '0px 7px' }}></div>
                    )}
                  </BellIcon>
                  <span
                    style={{
                      flexGrow: 0,
                      flexShrink: 1,
                      maxWidth: '200px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {watchlist.name ?? ''}{' '}
                  </span>
                </div>
              </TT>
            ),
            sortName: watchlist.name,
          },
        };
      })
      .sort(TC.sortDropDownMenuItems)
      .concat({
        id: 'createWatchlistItem',
        onClick: setActiveModal(ModalType.Create),
        value: {
          render: (
            <ItemDiv>
              <AddWatchlistIcon>
                <Add title="Create Watchlist" />
              </AddWatchlistIcon>
              <span>Create Watchlist</span>
            </ItemDiv>
          ),
          sortName: 'Create Watchlist',
        },
      })
      .concat({
        id: 'linkAccountItem',
        onClick: setActiveModal(ModalType.Link),
        value: {
          render: (
            <ItemDiv>
              <LinkAccountIcon>
                <Link title="Link Brokerage Portfolio" />
              </LinkAccountIcon>
              <span>Link Brokerage Portfolio</span>
            </ItemDiv>
          ),
          sortName: 'Link Brokerage Portfolio',
        },
      });

  const renderDropdown = () => {
    const items = createDropdownMenu(watchlists);
    const button = (
      <TT mouseEnterDelay={0.5} placement={'bottom'} title={watchlist?.name ?? ''}>
        <div style={{ display: 'flex', flex: '1 1 0', flexWrap: 'nowrap', overflow: 'hidden' }}>
          <BellIcon $padding={'0px 5px 0px 0px'}>
            {isWatchlistAlertEnabled(watchlist) ? (
              <Icon className={'WLBellIcon'} icon={faBell} />
            ) : (
              <div style={{ margin: '0px 7px' }}></div>
            )}
          </BellIcon>

          <span
            style={{ flexGrow: 0, flexShrink: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {watchlist?.name ?? ''}{' '}
          </span>
        </div>
      </TT>
    );

    return (
      <WatchlistDropDown
        button={{ type: 'toolbar', value: button }}
        className="TUTORIAL_Watchlist_Watchlists"
        dropdown={{ position: 'right' }}
        items={items}
        sort={false}
      />
    );
  };

  const renderTooltips = () => {
    if (watchlists.length === 0) {
      return (
        <TooltipLeft content="Add a watchlist" onClickHandler={promptWatchlistCreateIfEmpty} right="0px" top="70px" />
      );
    }

    const symbols = watchlist?.symbols;

    if (isNil(symbols) || isEmpty(symbols)) {
      return <Tooltip content="Search to add securities" left="15px" top="70px" />;
    }

    return null;
  };

  const renderButtons = () => {
    const hasAnyWatchlists = watchlists.length !== 0;
    // the reason for the !isEmpty check is that users
    // were getting confused, thinking that this button is
    // some kind of 'refresh' button, and clicking it when
    // their search was blank, effectively clearing the watchlist
    const showAlertsButton = hasAnyWatchlists;
    const showDownloadButton = hasAnyWatchlists;
    const showEditButton = hasAnyWatchlists;
    const showCreateButton = true;
    const showDeleteButton = hasAnyWatchlists;
    const showDropdown = hasAnyWatchlists;
    const showImportButton = hasAnyWatchlists;
    const showLinkButton = true;

    let alertsButton;
    let downloadButton;
    let editButton;
    let createButton;
    let deleteButton;
    let importButton;
    let linkButton;

    if (showDownloadButton) {
      downloadButton = (
        <button
          className="
            WidgetToolbar-btn
            WidgetToolbar-btn--icon
            WidgetToolbar-btn--primaryHover
            TUTORIAL_WatchlistToolbar-Sync
          "
          data-button-hint="Export watchlist to csv"
          onClick={exportToCSV}
        >
          <Download title="Download Watchlist" />
        </button>
      );
    }

    if (showImportButton) {
      importButton = (
        <div
          className="
          WidgetToolbar-btn
          WidgetToolbar-btn--icon
          WidgetToolbar-btn--primaryHover
          TUTORIAL_WatchlistToolbar-Sync
        "
        >
          <ImportButton data-button-hint="Import Watchlist" key="import" onClick={setActiveModal(ModalType.Import)}>
            <UpArrow title="Import Watchlist" />
          </ImportButton>
        </div>
      );
    }

    if (showLinkButton) {
      linkButton = (
        <div
          className="
          WidgetToolbar-btn
          WidgetToolbar-btn--icon
          WidgetToolbar-btn--primaryHover
          WidgetToolbar-btn--orangeHighlight
          TUTORIAL_LinkAccountToolbar-Sync
        "
        >
          <LinkButton
            data-button-hint="Link Brokerage Portfolio"
            key="linkportfolio"
            onClick={setActiveModal(ModalType.Link)}
          >
            <Link title="Link Brokerage Portfolio" />
          </LinkButton>
        </div>
      );
    }

    if (showAlertsButton) {
      alertsButton = (
        <button
          className={`
            WidgetToolbar-btn
            WidgetToolbar-btn--icon
            WidgetToolbar-btn--primaryHover
            TUTORIAL_WatchlistToolbar-Alerts
            ${isWatchlistAlertEnabled(watchlist) ? 'active' : ''}
          `}
          data-button-hint="Configure Alerts for this watchlist"
          onClick={setActiveModal(ModalType.Alerts)}
        >
          <Alert title="Configure Watchlist Alerts" />
        </button>
      );
    }

    if (showEditButton) {
      editButton = (
        <button
          className="
            WidgetToolbar-btn
            WidgetToolbar-btn--icon
            WidgetToolbar-btn--primaryHover
            TUTORIAL_WatchlistToolbar-Edit
          "
          data-button-hint="Rename watchlist"
          data-test="edit-watchlist-button"
          key="edit"
          onClick={setActiveModal(ModalType.Edit)}
        >
          <Edit title="Edit Watchlist" />
        </button>
      );
    }

    if (showCreateButton) {
      createButton = (
        <button
          className="
            WidgetToolbar-btn
            WidgetToolbar-btn--icon
            WidgetToolbar-btn--primaryHover
            TUTORIAL_WatchlistToolbar-Add
          "
          data-button-hint="Create a new watchlist"
          data-test="add-new-watchlist"
          key="create"
          onClick={setActiveModal(ModalType.Create)}
        >
          <Icon icon={faBooksMedical} style={{ fontSize: '0.9em' }} title={'New Watchlist'} />
        </button>
      );
    }

    if (showDeleteButton) {
      deleteButton = (
        <button
          className="
            WidgetToolbar-btn
            WidgetToolbar-btn--icon
            WidgetToolbar-btn--warnHover
            TUTORIAL_WatchlistToolbar-Remove
          "
          data-button-hint="Delete this watchlist"
          key="delete"
          onClick={setActiveModal(ModalType.Delete)}
        >
          <Trash title="Remove Watchlist" />
        </button>
      );
    }

    return (
      <div
        style={{
          display: 'flex',
          flexWrap: 'nowrap',
          maxWidth: '100%',
        }}
      >
        {alertsButton}
        {editButton}
        {downloadButton}
        {linkButton}
        {importButton}
        {createButton}
        {deleteButton}
        {showDropdown && renderDropdown()}
      </div>
    );
  };

  const updateWatchlist = React.useCallback(
    (watchlist: Watchlist): void => {
      watchlistManager.updateWatchlistSettings(watchlist);
    },
    [watchlistManager],
  );

  const createWatchlist = React.useCallback(
    async (watchlistName: string): Promise<void> => {
      const newWatchlist = await watchlistManager.createWatchlist(watchlistName, { emailSummary: true }, []);
      if (newWatchlist.ok) {
        props.onSelectWatchlist(newWatchlist.ok.watchlistId);
      }
    },
    [props, watchlistManager],
  );

  const linkedWatchlist = React.useCallback(
    (selectedWatchlist: Watchlist): void => {
      const onSelectWatchlist = props.onSelectWatchlist;
      onSelectWatchlist(selectedWatchlist.watchlistId);
    },
    [props.onSelectWatchlist],
  );

  const renderModal = () => {
    const { activeModal } = state;

    switch (activeModal) {
      case ModalType.Alerts:
        if (watchlist) {
          return <WatchlistAlerts closeModal={closeModal} updateWatchlist={updateWatchlist} watchlist={watchlist} />;
        }
        break;

      case ModalType.Create:
        return <WatchlistCreate closeModal={closeModal} createWatchlist={createWatchlist} />;

      case ModalType.Link:
        return <WatchlistLink closeModal={closeModal} linkedWatchlist={linkedWatchlist} />;

      case ModalType.Delete:
        if (watchlist) {
          return <WatchlistDelete closeModal={closeModal} watchlist={watchlist} />;
        }
        break;

      case ModalType.Import:
        if (watchlist) {
          return <WatchlistImport closeModal={closeModal} watchlistId={watchlist.watchlistId} />;
        }
        break;

      case ModalType.Edit:
        if (watchlist) {
          return (
            <WatchlistEdit
              closeModal={closeModal}
              updateWatchlist={updateWatchlist}
              watchlist={watchlist}
              watchlistName={watchlist ? watchlist.name : ''}
            />
          );
        }
        break;

      case ModalType.None:
        return null;

      default:
        assertUnreachable(activeModal);
        break;
    }

    return null;
  };

  const [emptyTags, setEmptyTags] = React.useState(() => []);
  const syncTags = React.useCallback(() => {
    setEmptyTags([]);
  }, []);

  const onTagsUpdated = React.useCallback(
    (inputSymbols: SearchItem[]) => {
      syncTags();
      if (props.selectedWatchlistId === null || watchlist === undefined) {
        return;
      }

      const nonDuplicateSymbols: StockSymbol[] = arrayDifference(
        inputSymbols.filter(isSymbolSearchItem).map(s => s.data.symbol),
        watchlist.symbols.map(s => s.symbol),
      );

      if (nonDuplicateSymbols.length) {
        watchlistManager.addTickersToWatchlist(watchlist, nonDuplicateSymbols);
      }
    },
    [props.selectedWatchlistId, syncTags, watchlist, watchlistManager],
  );

  const selectedItems = React.useRef<SymbolSearchItem[]>([]);
  React.useEffect(() => {
    selectedItems.current =
      watchlist?.symbols?.map(s => {
        const symItem = createSymbol({
          name: s.symbol,
          symbol: s.symbol,
          type: 'symbol',
        });
        return createSymbolSearchItem(symItem.tagId, symItem.label);
      }) ?? [];
  }, [watchlist?.symbols]);

  const symbolModule = useSymbolSearchModule();
  const overriddenSymbolModule = React.useMemo(
    () => ({
      ...symbolModule,
      autocomplete: {
        ...symbolModule.autocomplete,
        query: (text: string, limit: number, selectedItem?: SearchItem[]) => {
          return symbolModule.autocomplete.query(text, limit, [...(selectedItem ?? []), ...selectedItems.current]);
        },
      },
    }),
    [symbolModule],
  );
  const modules = React.useMemo(() => [overriddenSymbolModule], [overriddenSymbolModule]);

  const dropDownTool = useDropdownTool();
  const clipboardTool = useClipboardTool();
  const tools = React.useMemo(() => [dropDownTool, clipboardTool], [dropDownTool, clipboardTool]);
  return (
    <div>
      <div
        className={classnames('Search WidgetToolbar WidgetToolbar--watchlist TUTORIAL_Search-input_Watchlist', {
          'Search--hidden': !props.selectedWatchlistId,
        })}
      >
        <div onClick={promptWatchlistCreateIfEmpty} style={{ flex: 1, flexBasis: '40%' }}>
          <TagSearch
            iconOverride={<WatchlistSearchIcon />}
            modules={modules}
            onTagsUpdated={onTagsUpdated}
            placeholder={'Add $Symbol or Security Name... To Watchlist'}
            tags={emptyTags}
            tools={tools}
          />
        </div>
        {renderButtons()}
      </div>

      {renderTooltips()}
      {renderModal()}
    </div>
  );
};

const ItemDiv = styled.div`
  display: flex;
  flex: 0 0 0px;
`;

const AddWatchlistIcon = styled.div`
  margin-right: 0.5em;
`;

const LinkAccountIcon = styled.div`
  margin-right: 0.5em;
  svg {
    height: 1em;
    transform: scale(2);
    width: 1em;
  }
`;

const BellIcon = styled.span<{ $padding: string }>`
  vertical-align: unset;
  padding: ${props => props.$padding};
  .WLBellIcon {
    vertical-align: text-bottom;
  }
`;

const WatchlistDropDown = styled(TC.DropdownMenu)`
  flex-basis: 250px;
  flex-grow: 1;
  max-width: 100%;
  min-width: 185px;
`;

const ImportButton = styled.button`
  width: 100%;
  height: 100%;
`;

const LinkButton = styled.button`
  width: 100%;
  height: 100%;
`;

const TooltipLeft = styled(Tooltip)`
  &:after,
  &:before {
    left: 92%;
  }
`;

const WatchlistSearchIcon = styled(Add)`
  /* fill: ${props => props.theme.colors.foregroundInactive}; */
  fill: ${props => props.theme.colors.accent};
  margin: 8px;
  font-size: 22px;
`;
